import React from "react";
import { Button, Tag } from "antd";
import { CarOutlined, CheckCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * "Nog te doen" on the work-day view of the orders page (planning/werkdag-en-printstatus.md).
 *
 * Orders for tomorrow keep arriving until 12:00, after the first round has been exported and
 * printed - and the second round is often done by someone else on the same account. This bar is
 * the hand-over: per step how far the work day is, and one button per step that acts on exactly
 * the orders that step still has to do, whatever is selected in the table. After 12:00 you work
 * through it from top to bottom.
 *
 * Picking is not a step: the CMS cannot know that until the pick app (HVC-42) is live.
 *
 * All numbers and order lists come from `workDayProgress` (functions/fulfilment.js); this
 * component only renders them and hands the lists back through the callbacks.
 *
 * With a group filter on (VCE Landelijk, VCE Regionaal, HVO) the bar counts that group only, and
 * `filterNote` says so: a green "Klaar" must never hide the other groups' open work.
 */
const Row = ({ title, done, total, unit, children, note }) => {
  const { t } = useTranslation();
  const complete = total > 0 && done === total;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
      <span style={{ fontWeight: 600, minWidth: 140 }}>{title}</span>
      <span>
        <span style={{ fontWeight: 600 }}>{done}</span>
        <span style={{ color: "#8c8c8c" }}> {t("workday.todo.of_total", { total, unit })}</span>
      </span>
      {complete && (
        <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
          {t("workday.todo.complete")}
        </Tag>
      )}
      {children}
      {!!note && <span style={{ color: "#8c8c8c", fontSize: 12 }}>{note}</span>}
    </div>
  );
};

const WorkDayTodo = ({
  progress,
  loading,
  onExportSooCool,
  onExportTrunkrs,
  onExportOptimoRoute,
  onPrintSooCoolLabels,
  onPrintOwnLabels,
  onPrintPackingSlips,
  filterNote,
  onShowAllGroups,
}) => {
  const { t } = useTranslation();
  // A group with nothing in it still says that it is on: otherwise the bar simply vanishes while
  // the other groups have work waiting.
  if (!progress || (progress.total === 0 && !filterNote)) return null;
  const isEmpty = progress.total === 0;

  const { carrier, labels, packingSlips } = progress;

  return (
    <div
      data-testid="work-day-todo"
      style={{
        border: "1px solid #d9d9d9",
        padding: "12px 16px",
        borderRadius: 4,
        marginBottom: 16,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{t("workday.todo.title")}</span>
        {!!filterNote && (
          <>
            <Tag color="orange" style={{ margin: 0 }}>
              {filterNote}
            </Tag>
            {!!onShowAllGroups && (
              <Button size="small" type="link" style={{ padding: 0 }} onClick={onShowAllGroups}>
                {t("order_group.show_all")}
              </Button>
            )}
          </>
        )}
      </div>

      {carrier.total > 0 && (
        <Row
          title={t("workday.todo.carrier")}
          done={carrier.done}
          total={carrier.total}
          unit={t("workday.todo.unit_send")}
        >
          {carrier.toSooCool.length > 0 && (
            <Button
              size="small"
              type="primary"
              icon={<CarOutlined />}
              loading={loading}
              onClick={() => onExportSooCool(carrier.toSooCool)}
            >
              {t("workday.todo.export_soocool", { count: carrier.toSooCool.length })}
            </Button>
          )}
          {(carrier.toOptimoRoute || []).length > 0 && (
            <Button
              size="small"
              type="primary"
              icon={<CarOutlined />}
              loading={loading}
              onClick={() => onExportOptimoRoute(carrier.toOptimoRoute)}
            >
              {t("workday.todo.export_optimoroute", { count: carrier.toOptimoRoute.length })}
            </Button>
          )}
          {carrier.toTrunkrs.length > 0 && (
            <Button
              size="small"
              type="primary"
              icon={<CarOutlined />}
              loading={loading}
              onClick={() => onExportTrunkrs(carrier.toTrunkrs)}
            >
              {t("workday.todo.export_trunkrs", { count: carrier.toTrunkrs.length })}
            </Button>
          )}
        </Row>
      )}

      {!isEmpty && (
        <Row
          title={t("workday.todo.labels")}
          done={labels.done}
          total={labels.total}
          unit={t("workday.todo.unit_printed")}
          note={
            labels.awaitingExport.length > 0
              ? t("workday.todo.awaiting_export", { count: labels.awaitingExport.length })
              : null
          }
        >
          {labels.soocool.length > 0 && (
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={loading}
              onClick={() => onPrintSooCoolLabels(labels.soocool)}
            >
              {t("workday.todo.print_soocool_labels", { count: labels.soocool.length })}
            </Button>
          )}
          {labels.own.length > 0 && (
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={loading}
              onClick={() => onPrintOwnLabels(labels.own)}
            >
              {t("workday.todo.print_own_labels", { count: labels.own.length })}
            </Button>
          )}
        </Row>
      )}

      {!isEmpty && (
        <Row
          title={t("workday.todo.packing_slips")}
          done={packingSlips.done}
          total={packingSlips.total}
          unit={t("workday.todo.unit_printed")}
        >
          {packingSlips.open.length > 0 && (
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={loading}
              onClick={() => onPrintPackingSlips(packingSlips.open)}
            >
              {t("workday.todo.print_packing_slips", { count: packingSlips.open.length })}
            </Button>
          )}
        </Row>
      )}
    </div>
  );
};

export default WorkDayTodo;
